import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Upload, List, message, Image } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { binaryContentsHooks as cHooks } from "../hooks";

const ExpertQADetail = ({ visible, onClose, qa }) => {
  const [comments, setComments] = useState([]); // 댓글 리스트
  const [commentImageList, setCommentImageList] = useState([]); // 댓글 이미지
  const [qaImageList, setQaImageList] = useState([]); // QA 기본 이미지
  const [editingComment, setEditingComment] = useState(null); // 수정 중인 댓글
  const [form] = Form.useForm();

  // QA 이미지 처리: 문자열을 배열로 변환
  useEffect(() => {
    if (qa?.qa_refer_image) {
      const images = qa.qa_refer_image.split(",").map((url, index) => ({
        uid: `existing-${index}`,
        name: `image-${index}`,
        url: url,
        isNew: false,
      }));
      setQaImageList(images);
    }
  }, [qa]);

  // 댓글 가져오기
  const fetchComments = async () => {
    try {
      const response = await cHooks.expertQACommentList({ id: qa.id });
      if (response.IsError) {
        throw new Error("api error");
      }
      setComments(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      console.error("댓글 데이터를 가져오는 중 오류가 발생했습니다.", error);
      setComments([]);
    }
  };

  // 모달 열릴 때 댓글 불러오기
  useEffect(() => {
    if (visible && qa?.id) {
      fetchComments();
    }
  }, [visible, qa]);

  // QA 삭제
  const handleDeleteQA = async () => {
    try {
      const res = await cHooks.expertQADelete({ id: qa.id });
      if (res.IsError) {
        throw new Error("api error");
      }
      message.success("QA가 삭제되었습니다.");
      onClose();
    } catch (error) {
      message.error("QA 삭제에 실패했습니다.");
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async (commentId) => {
    try {
      const res = await cHooks.expertQACommentDelete({ id: commentId });
      if (res.IsError) {
        throw new Error("api error");
      }
      message.success("댓글이 삭제되었습니다.");
      fetchComments();
    } catch (error) {
      message.error("댓글 삭제에 실패했습니다.");
    }
  };

  // 댓글 추가/수정
  const handleAddOrUpdateComment = async () => {
    try {
      const values = await form.validateFields(["qa_comment"]);
      const images = commentImageList.map((file) =>
        file.isNew ? file.url.split(",")[1] : file.url
      );

      const payload = {
        id: editingComment?.id || qa.id,
        qaComment: values.qa_comment,
        qaCommentReferImage: images,
      };

      if (editingComment) {
        const res = await cHooks.expertQACommentUpdate(payload);
        if (res.IsError) {
          throw new Error("api error");
        }
        message.success("댓글이 수정되었습니다.");
      } else {
        const res = await cHooks.expertQACommentCreate(payload);
        if (res.IsError) {
          throw new Error("api error");
        }
        message.success("댓글이 추가되었습니다.");
      }

      fetchComments();
      form.resetFields();
      setCommentImageList([]);
      setEditingComment(null);
    } catch (error) {
      message.error("댓글 추가/수정에 실패했습니다.");
    }
  };

  return (
    <Modal
      title="Expert QA 상세"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {/* QA 기본 정보 */}
      <div style={{ marginBottom: "20px" }}>
        <h2>{qa.qa_title}</h2>
        <p>{qa.qa_description}</p>
        {qaImageList.length > 0 && (
          <Image.PreviewGroup>
            {qaImageList.map((img) => (
              <Image
                key={img.uid}
                src={img.url}
                alt={`QA 이미지`}
                width={100}
                height={100}
                style={{ marginRight: "8px", marginTop: "5px" }}
              />
            ))}
          </Image.PreviewGroup>
        )}
        <Button danger style={{ marginTop: "10px" }} onClick={handleDeleteQA}>
          QA 삭제하기
        </Button>
      </div>

      {/* 댓글 목록 */}
      <h3 style={{ marginTop: "20px" }}>댓글 목록</h3>
      <List
        dataSource={comments}
        renderItem={(comment) => {
          const images = comment.qa_comment_refer_image
            ? comment.qa_comment_refer_image.split(",")
            : [];
          return (
            <List.Item
              actions={[
                <Button
                  type="link"
                  onClick={() => {
                    setEditingComment(comment);
                    form.setFieldsValue({ qa_comment: comment.qa_comment });
                    setCommentImageList(
                      images.map((url, index) => ({
                        uid: `existing-${index}`,
                        name: `comment-image-${index}`,
                        url: url,
                        isNew: false,
                      }))
                    );
                  }}
                >
                  수정
                </Button>,
                <Button
                  type="link"
                  danger
                  onClick={() => handleDeleteComment(comment.id)}
                >
                  삭제
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={`${comment.role === "admin" ? "전문가" : "유저"}: ${
                  comment.user_name
                }`}
                description={
                  <div>
                    <p>{comment.qa_comment}</p>
                    <Image.PreviewGroup>
                      {images.map((url, index) => (
                        <Image
                          key={index}
                          src={url}
                          alt={`댓글 이미지 ${index + 1}`}
                          width={100}
                          height={100}
                          style={{
                            marginRight: "8px",
                            marginTop: "5px",
                            objectFit: "cover",
                          }}
                        />
                      ))}
                    </Image.PreviewGroup>
                  </div>
                }
              />
            </List.Item>
          );
        }}
      />

      {/* 댓글 작성/수정 */}
      <Form layout="vertical" form={form} onFinish={handleAddOrUpdateComment}>
        <Form.Item
          name="qa_comment"
          label={editingComment ? "댓글 수정" : "댓글 작성"}
          rules={[{ required: true, message: "댓글 내용을 입력해주세요." }]}
        >
          <Input.TextArea rows={2} placeholder="댓글 내용을 입력하세요" />
        </Form.Item>
        <Form.Item label="첨부 이미지">
          <Upload
            listType="picture"
            multiple
            beforeUpload={(file) => {
              const reader = new FileReader();
              reader.onload = (e) => {
                setCommentImageList((prev) => [
                  ...prev,
                  {
                    uid: file.uid,
                    name: file.name,
                    url: e.target.result,
                    isNew: true,
                  },
                ]);
              };
              reader.readAsDataURL(file);
              return false; // 업로드 방지
            }}
            onRemove={(file) => {
              setCommentImageList((prev) =>
                prev.filter((img) => img.uid !== file.uid)
              );
            }}
            fileList={commentImageList}
          >
            <Button icon={<UploadOutlined />}>이미지 추가</Button>
          </Upload>
        </Form.Item>
        <Button type="primary" htmlType="submit">
          {editingComment ? "댓글 수정" : "댓글 추가"}
        </Button>
      </Form>
    </Modal>
  );
};

export default ExpertQADetail;
